import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';
import { submitLocalForm } from '../../../lib/localForms';

const EQUIPMENT_OPTIONS = [
  { id: 'kitchen', label: 'Kitchen', icon: 'ri-restaurant-line' },
  { id: 'sound_system', label: 'Sound System', icon: 'ri-speaker-line' },
  { id: 'streaming', label: 'Streaming', icon: 'ri-live-line' },
  { id: 'lighting', label: 'Lighting', icon: 'ri-lightbulb-line' },
  { id: 'visuals', label: 'Visuals (TVs, Projector)', icon: 'ri-projector-2-line' },
];

export default function EventBookingFormPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);

  const toggleEquipment = (id: string) => {
    setSelectedEquipment(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncodedData = new URLSearchParams();

    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    if (selectedEquipment.length > 0) {
      urlEncodedData.append('equipment_needed', selectedEquipment.join(', '));
    } else {
      urlEncodedData.append('equipment_needed', 'None');
    }

    try {
      const response = await submitLocalForm('Event Booking Form', urlEncodedData);

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setSelectedEquipment([]);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <PageHero
        title="Event Booking"
        subtitle="Reserve a space at our church for your event or gathering"
        backgroundImage="/assets/readdy/event-booking-hero-001-21d7480691d0.jpg"
      />

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Notice banner */}
        <div className="bg-burgundy-50 border border-burgundy-200 rounded-xl p-5 mb-10 flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-burgundy-700 text-2xl"></i>
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-1">Booking Approval Required</p>
            <p className="text-sm text-gray-600">
              Nothing is confirmed until <strong>Father Anthony</strong> has approved the requested date and time, unless it has already been confirmed beforehand. You will be contacted within 2–3 business days to confirm your reservation.
            </p>
          </div>
        </div>

        <form
          id="event-booking-form"
          data-local-form
          onSubmit={handleSubmit}
          className="space-y-8"
        >
          {/* ── Contact Information ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <h3 className="text-xl font-serif font-bold text-[#3d1a1e] mb-6 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                <i className="ri-user-line text-lg"></i>
              </div>
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                <input type="text" name="first_name" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Your first name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                <input type="text" name="last_name" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Your last name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                <input type="tel" name="phone" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="(514) 000-0000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                <input type="email" name="email" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="your@email.com" />
              </div>
            </div>
          </div>

          {/* ── Event Details ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <h3 className="text-xl font-serif font-bold text-[#3d1a1e] mb-6 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                <i className="ri-calendar-event-line text-lg"></i>
              </div>
              Event Details
            </h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title of Event / Reason for Reservation <span className="text-red-500">*</span></label>
                <input type="text" name="event_title" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="e.g. Family Gathering, Sunday School Retreat, Bible Study..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requested Date <span className="text-red-500">*</span></label>
                  <input type="date" name="requested_date" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requested Time <span className="text-red-500">*</span></label>
                  <input type="time" name="requested_time" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
                </div>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-information-line text-gold-600 text-lg flex-shrink-0"></i>
                <p className="text-sm text-gray-600">Your requested date and time are <strong>subject to Father Anthony's approval</strong>. Nothing is confirmed until he has reviewed and approved your request, unless already confirmed beforehand.</p>
              </div>
            </div>
          </div>

          {/* ── Location Selection ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <h3 className="text-xl font-serif font-bold text-[#3d1a1e] mb-6 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              Booking Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: 'Basement', label: 'Basement', icon: 'ri-building-4-line', desc: 'Large gathering hall with open floor space' },
                { value: 'Church', label: 'Church', icon: 'ri-community-line', desc: 'Main sanctuary for services and ceremonies' },
                { value: 'Room', label: 'Room', icon: 'ri-door-line', desc: 'Private meeting or classroom room' },
              ].map(loc => (
                <label key={loc.value} className="relative cursor-pointer group">
                  <input type="radio" name="booking_location" value={loc.value} required className="sr-only peer" />
                  <div className="h-full border-2 border-stone-200 rounded-xl p-5 peer-checked:border-burgundy-700 peer-checked:bg-burgundy-50 group-hover:border-gold-400 transition-all">
                    <div className="w-10 h-10 flex items-center justify-center bg-stone-100 rounded-lg mb-3">
                      <i className={`${loc.icon} text-xl text-gray-600`}></i>
                    </div>
                    <p className="font-semibold text-gray-900 mb-1">{loc.label}</p>
                    <p className="text-xs text-gray-500">{loc.desc}</p>
                  </div>
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full border-2 border-stone-300 peer-checked:border-burgundy-700 peer-checked:bg-burgundy-700 flex items-center justify-center transition-all">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* ── Equipment Needed ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <h3 className="text-xl font-serif font-bold text-[#3d1a1e] mb-2 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                <i className="ri-tools-line text-lg"></i>
              </div>
              Equipment Needed
            </h3>
            <p className="text-sm text-gray-500 mb-6 ml-12">Select all equipment you&apos;ll require for your event.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {EQUIPMENT_OPTIONS.map(opt => {
                const isSelected = selectedEquipment.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleEquipment(opt.id)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-800'
                        : 'border-stone-200 bg-stone-50 text-gray-700 hover:border-gold-400'
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isSelected ? 'bg-gold-100' : 'bg-white'}`}>
                      <i className={`${opt.icon} text-lg ${isSelected ? 'text-gold-600' : 'text-gray-500'}`}></i>
                    </div>
                    <span className="text-sm font-medium">{opt.label}</span>
                    {isSelected && (
                      <i className="ri-checkbox-circle-fill text-burgundy-700 ml-auto flex-shrink-0"></i>
                    )}
                  </button>
                );
              })}
            </div>

            {selectedEquipment.length > 0 && (
              <div className="mt-4 p-3 bg-burgundy-50 rounded-lg border border-burgundy-100">
                <p className="text-xs text-burgundy-700">
                  <strong>Selected:</strong> {selectedEquipment.map(id => EQUIPMENT_OPTIONS.find(o => o.id === id)?.label).join(', ')}
                </p>
              </div>
            )}
          </div>

          {/* ── Notes & Special Requests ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <h3 className="text-xl font-serif font-bold text-[#3d1a1e] mb-2 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                <i className="ri-file-text-line text-lg"></i>
              </div>
              Notes &amp; Special Requests
            </h3>
            <p className="text-sm text-gray-500 mb-5 ml-12">Optional — share any extra details, special arrangements, or requests for the church office.</p>
            <textarea
              name="notes"
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent resize-none"
              placeholder="e.g. We will need extra chairs, the event is for approximately 50 people, please keep the side entrance open..."
            />
            <p className="text-xs text-gray-400 mt-2 text-right">Max 500 characters</p>
          </div>

          {/* ── Status Messages ── */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="text-green-800 font-semibold">Booking request submitted!</p>
                <p className="text-green-700 text-sm mt-1">
                  Thank you! Your request is pending approval. We will contact you within 2–3 business days to confirm your booking.
                </p>
              </div>
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="text-red-800 font-semibold">Submission failed</p>
                <p className="text-red-700 text-sm mt-1">Please try again or contact the church office directly.</p>
              </div>
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/resources')}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 text-sm font-medium text-white bg-burgundy-700 rounded-xl hover:bg-burgundy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </span>
              ) : 'Submit Booking Request'}
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
}
